import { useState, useEffect } from "react";
import { SelfCertEntityFormModel } from "../Models/SelfCertEntityModels";
import { SelfCertEntityService } from "../services/SelfCertEntityService";

export interface UseSelfCertEntityFormOptions {
  autoSaveDraft?: boolean;
  autoSaveInterval?: number;
}

export interface UseSelfCertEntityFormReturn {
  model: SelfCertEntityFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (fieldName: string, value: any) => void;
  updateMultipleFields: (fields: Record<string, any>) => void;
  validate: () => boolean;
}

export const useSelfCertEntityForm = (
  initialData?: any,
  options: UseSelfCertEntityFormOptions = {}
): UseSelfCertEntityFormReturn => {
  const [model, setModel] = useState<SelfCertEntityFormModel>(() => {
    if (initialData) {
      return SelfCertEntityService.fromApiResponse(initialData);
    }
    return new SelfCertEntityFormModel();
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(true);

  const updateField = (fieldName: string, value: any) => {
    setModel((prevModel) => {
      const newModel = new SelfCertEntityFormModel(prevModel);
      newModel[fieldName] = value;
      return newModel;
    });
  };

  const updateMultipleFields = (fields: Record<string, any>) => {
    setModel((prevModel) => {
      const newModel = new SelfCertEntityFormModel(prevModel);
      Object.entries(fields).forEach(([key, value]) => {
        newModel[key] = value;
      });
      return newModel;
    });
  };

  const validate = (): boolean => {
    const validationResult = model.validate();
    // Convert string array to Record<string, string> format
    const errorRecord: Record<string, string> = {};
    if (validationResult.errors && Array.isArray(validationResult.errors)) {
      validationResult.errors.forEach((error, index) => {
        errorRecord[`error_${index}`] = error;
      });
    }
    setErrors(errorRecord);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  };

  // Auto-validation on model changes
  useEffect(() => {
    validate();
  }, [model]);

  return {
    model,
    isLoading: false,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
